import { useEffect, useState } from 'react';

const DISMISS_KEY = 'fmp.install-banner.dismissed';

/**
 * iOS has no install prompt -- the user must tap Share then "Add to Home
 * Screen" (plan section 10). Nothing can trigger that programmatically, so the
 * only thing to build is a small, dismissible instruction.
 *
 * Shown only when it is actually actionable: iOS Safari, not already installed,
 * not previously dismissed.
 */
export function InstallBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isStandalone() || !isIosSafari()) return;
    try {
      if (localStorage.getItem(DISMISS_KEY)) return;
    } catch {
      /* private mode: just show it */
    }
    setShow(true);
  }, []);

  if (!show) return null;

  return (
    <div className="mx-4 mt-4 rounded-xl border border-border-interactive bg-bg-raised px-4 py-3 text-sm shadow-sm">
      <p className="font-medium">Add this to your home screen</p>
      <p className="mt-1 text-text-secondary">
        Tap the Share button, then <strong>Add to Home Screen</strong>. It opens full screen and
        loads faster.
      </p>
      <button
        type="button"
        className="mt-2 min-h-11 text-sm font-medium text-text-muted underline underline-offset-2"
        onClick={() => {
          try {
            localStorage.setItem(DISMISS_KEY, '1');
          } catch {
            /* nothing to do */
          }
          setShow(false);
        }}
      >
        Not now
      </button>
    </div>
  );
}

function isStandalone(): boolean {
  // iOS uses the legacy navigator.standalone; everything else uses the media query.
  const iosStandalone = (navigator as { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia('(display-mode: standalone)').matches;
}

function isIosSafari(): boolean {
  const ua = navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && 'ontouchend' in document);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return isIos && isSafari;
}
